import React from 'react';
import {
  OFFERED,
  PROCEDURE_COST_RANGE,
  estimatedContributionCents,
  formatPrice,
} from '../../products/editions';

/** How a deck's price splits between estimated manufacturing cost and the voucher pool. */
const DeckImpact: React.FC = () => (
  <section id="impact" aria-labelledby="impact-heading" className="py-16">
    <div className="container mx-auto px-6">
      <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
        <div>
          <h2 id="impact-heading" className="text-3xl font-bold mb-6 text-gray-800">Every Deck Helps Fund Veterinary Care</h2>
          <div className="space-y-4 text-lg text-gray-700">
            <p>
              Spaying and neutering can cost approximately {formatPrice(PROCEDURE_COST_RANGE.lowCents)}–
              {formatPrice(PROCEDURE_COST_RANGE.highCents)} per procedure at low-cost clinics, depending on the animal and
              procedure.
            </p>
            <p>For families facing financial challenges, these costs can become a barrier to care.</p>
            <p>
              Pawsitive Future Foundation works to reduce those barriers by funding veterinary care vouchers, helping families
              access spay and neuter services and reducing unwanted litters.
            </p>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-xl font-bold text-gray-800">Estimated voucher contribution per deck</h3>
          <p className="mt-1 text-sm text-gray-600">Preliminary planning estimates, not guaranteed donation amounts.</p>
          <ul className="mt-6 space-y-5">
            {OFFERED.map((edition) => {
              const contribution = estimatedContributionCents(edition);
              return (
                <li key={edition.id}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="font-medium text-gray-800">
                      {edition.shortName} <span className="text-gray-500">· {formatPrice(edition.priceCents)} deck</span>
                    </span>
                    <span className="text-lg font-bold tabular-nums text-secondary">{formatPrice(contribution)}</span>
                  </div>
                  <div className="mt-2 flex h-3 overflow-hidden rounded-full bg-gray-200" aria-hidden="true">
                    <div className="bg-secondary" style={{ width: `${(contribution / edition.priceCents) * 100}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-600" aria-hidden="true">
            <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-secondary" />Est. voucher contribution</span>
            <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-gray-200" />Est. manufacturing cost</span>
          </div>
        </div>
      </div>

      <p className="mt-10 rounded-lg border border-line bg-gray-50 p-4 text-sm text-gray-600">
        Estimated voucher contributions are based on preliminary manufacturing budgets. Final contributions may vary with
        manufacturing, freight, fulfillment, payment processing, and other applicable costs. 100% of net profits will support
        the veterinary care voucher pool.
      </p>
    </div>
  </section>
);

export default DeckImpact;
